import React, { ReactNode } from 'react'
import { Navigate, useLocation } from 'react-router-dom'
import { Spin } from 'antd'
import { useAuth } from '../../hooks/useAuth'
import { POST_LOGIN_REDIRECT_STORAGE_KEY } from '../../utils/axiosConfig'
import { hasAllPermissions, hasAnyPermissions } from '../../utils/permissions'
import { AccessControlledRoute } from './AccessControlledRoute'

interface PermissionRouteProps {
    permissions: readonly string[]
    requireAny?: boolean
    children: ReactNode
    redirectTo?: string
    showAccessDenied?: boolean
    accessDeniedMessage?: string
    accessDeniedDescription?: string
    accessDeniedActionText?: string
}

export const PermissionRoute: React.FC<PermissionRouteProps> = ({
    permissions,
    requireAny = false,
    children,
    redirectTo = '/',
    showAccessDenied = true,
    accessDeniedMessage,
    accessDeniedDescription,
    accessDeniedActionText,
}) => {
    const location = useLocation()
    const { user, authStatus } = useAuth()

    if (authStatus === 'checking') {
        return (
            <div
                style={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    minHeight: '200px',
                }}
            >
                <Spin size="large" />
            </div>
        )
    }

    if (authStatus !== 'authenticated' || !user) {
        sessionStorage.setItem(
            POST_LOGIN_REDIRECT_STORAGE_KEY,
            `${location.pathname}${location.search}${location.hash}`
        )
        return <Navigate to="/login" replace />
    }

    const hasAccess = requireAny
        ? hasAnyPermissions(user, permissions)
        : hasAllPermissions(user, permissions)

    return (
        <AccessControlledRoute
            canAccess={hasAccess}
            redirectTo={redirectTo}
            showAccessDenied={showAccessDenied}
            accessDeniedMessage={accessDeniedMessage}
            accessDeniedDescription={accessDeniedDescription}
            accessDeniedActionText={accessDeniedActionText}
        >
            {children}
        </AccessControlledRoute>
    )
}
